import React, { useMemo } from "react";
import { restaurantOptionsByPark, getRestaurantsByDiningPreferences, getRestaurantsByCuisinePreferences } from "../../data/diningOptionsByPark";
import Tooltip from "./Tooltip";

const VALID_THEME_PARKS = [
  "Magic Kingdom",
  "EPCOT",
  "Hollywood Studios",
  "Animal Kingdom",
  "Disney Springs"
];

const getAllRestaurantsByParks = (parks) => {
  const allRestaurants = [];
  parks.forEach((parkName) => {
    if (VALID_THEME_PARKS.includes(parkName)) {
      const parkRestaurants = restaurantOptionsByPark[parkName] || [];
      allRestaurants.push(...parkRestaurants);
    }
  });
  return allRestaurants;
};

const RestaurantSelector = ({
  field,
  selectedParks,
  selected,
  formData,
  showAllRestaurants,
  setShowAllRestaurants,
  handleChange,
}) => {
  // Get dining and cuisine preferences for filtering
  const cuisinePreferences = useMemo(
    () => formData.diningPreferences?.cuisinePreferences || [],
    [formData.diningPreferences?.cuisinePreferences]
  );
  const restaurantStrategy = useMemo(
    () => formData.diningPreferences?.restaurantStrategy || [],
    [formData.diningPreferences?.restaurantStrategy]
  );

  // Filter restaurants based on preferences (meal-only; no snack/cart/kiosk)
  const filteredRestaurants = useMemo(() => {
    const shouldShowAll = Boolean(showAllRestaurants);
    if (restaurantStrategy.includes("See it All – No preference, show me everything") || shouldShowAll) {
      return getAllRestaurantsByParks(selectedParks);
    }

    let restaurants = getRestaurantsByDiningPreferences(selectedParks, restaurantStrategy);

    if (cuisinePreferences.length > 0 && !cuisinePreferences.includes("No Preference") && !cuisinePreferences.includes("No Preference – Let the agent choose")) {
      const cuisineFiltered = getRestaurantsByCuisinePreferences(selectedParks, cuisinePreferences);
      if (restaurants.length > 0) {
        const restaurantNames = new Set(restaurants.map(r => r.name));
        restaurants = cuisineFiltered.filter(r => restaurantNames.has(r.name));
      } else {
        restaurants = cuisineFiltered;
      }
    }

    return restaurants;
  }, [selectedParks, restaurantStrategy, cuisinePreferences, showAllRestaurants]);

  // Legacy snack/cart/kiosk selections: keep in state but show as unavailable (do not crash)
  const availableNames = useMemo(() => new Set(filteredRestaurants.map(r => r.name)), [filteredRestaurants]);
  const unavailableSelections = useMemo(
    () => (selected || []).filter((name) => !availableNames.has(name)),
    [selected, availableNames]
  );

  // Group restaurants by park
  const restaurantsByPark = useMemo(() => {
    const grouped = {};
    filteredRestaurants.forEach(restaurant => {
      const park = restaurant.location;
      if (!grouped[park]) {
        grouped[park] = [];
      }
      grouped[park].push(restaurant);
    });
    return grouped;
  }, [filteredRestaurants]);

  const handleRestaurantToggle = (restaurantName) => {
    const currentSelected = selected || [];
    const updated = currentSelected.includes(restaurantName)
      ? currentSelected.filter(name => name !== restaurantName)
      : [...currentSelected, restaurantName];
    handleChange(field.id, updated);
  };

  // Badge helpers (match RideSelector)
  const getTypeBadge = (type) => (
    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700 font-semibold">{type}</span>
  );
  const getPriceBadge = (price) => {
    let color = "bg-gray-200 text-gray-700";
    if (price === "$") color = "bg-green-100 text-green-700";
    if (price === "$$") color = "bg-yellow-100 text-yellow-700";
    if (price === "$$$") color = "bg-orange-100 text-orange-700";
    if (price === "$$$$") color = "bg-red-100 text-red-700";
    return (
      <span className={`ml-2 px-2 py-0.5 text-xs rounded-full font-semibold ${color}`}>{price}</span>
    );
  };

  if (!restaurantStrategy.length) {
    return (
      <p className="text-sm italic text-gray-700">
        Please answer the restaurant strategy question above to begin selecting your must-try restaurants.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-row gap-3 items-center mb-2">
        <button
          type="button"
          onClick={() => setShowAllRestaurants(!showAllRestaurants)}
          className="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700"
        >
          {showAllRestaurants ? "Show Strategy Matches Only" : "Show All Restaurants"}
        </button>
      </div>

      {/* Restaurant Count */}
      <div className="mb-2 text-sm text-gray-600">
        {filteredRestaurants.length} restaurant{filteredRestaurants.length !== 1 ? 's' : ''} found
        {selected.length > 0 && ` • ${selected.length} selected`}
      </div>

      {/* Restaurants by Park */}
      {Object.entries(restaurantsByPark).map(([parkName, restaurants]) => (
        <div key={parkName} className="p-4 mb-6 rounded-md border bg-white/80">
          <h3 className="mb-2 text-lg font-bold text-textPrimary">
            {parkName}
          </h3>
          <div className="space-y-2">
            {restaurants.map((restaurant) => {
              const isSelected = selected.includes(restaurant.name);
              return (
                <label
                  key={restaurant.name}
                  className="flex gap-2 items-start text-sm cursor-pointer text-textPrimary"
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleRestaurantToggle(restaurant.name)}
                    className="mt-1 accent-primary"
                  />
                  <div className="flex flex-col items-start gap-0.5 w-full">
                    <div className="flex flex-wrap gap-1 items-center">
                      <span className="font-medium">{restaurant.name}</span>
                      {getTypeBadge(restaurant.serviceLabel ?? (restaurant.service === 'QS' ? 'Quick Service' : 'Table Service'))}
                      {getPriceBadge(restaurant.price)}
                      {restaurant.warningNote && (
                        <Tooltip message={restaurant.warningNote}>
                          <span className="text-red-600 cursor-pointer text-[10px] border border-red-600 rounded-full px-[0.3rem] pb-[1px] leading-none ml-1">
                            !
                          </span>
                        </Tooltip>
                      )}
                    </div>
                    <span className="text-xs text-gray-500">{restaurant.cuisine}</span>
                    {restaurant.tags && restaurant.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {restaurant.tags.slice(0, 2).map((tag, index) => (
                          <span
                            key={index}
                            className="inline-block px-1 py-0.5 text-xs bg-gray-100 text-gray-700 rounded"
                          >
                            {tag}
                          </span>
                        ))}
                        {restaurant.tags.length > 2 && (
                          <span className="text-xs text-gray-500">
                            +{restaurant.tags.length - 2} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      ))}

      {filteredRestaurants.length === 0 && (
        <div className="py-8 text-center text-gray-500">
          <p>No restaurants found matching your criteria.</p>
          <p className="mt-2 text-sm">Try adjusting your search or preferences.</p>
        </div>
      )}

      {unavailableSelections.length > 0 && (
        <div className="p-4 mt-4 bg-amber-50 rounded-md border border-amber-200">
          <h4 className="text-sm font-semibold text-amber-800">Not shown in current filter</h4>
          <p className="mt-1 text-xs text-amber-700">
            These restaurants don&apos;t appear in the list above (e.g. they don&apos;t match your current strategy or cuisine filters), but they will still be included in your itinerary. ParkPro will schedule them when you visit those parks.
          </p>
          <div className="flex flex-wrap gap-1 mt-2">
            {unavailableSelections.map((name) => (
              <span key={name} className="px-2 py-1 text-xs text-amber-800 bg-amber-100 rounded">
                {name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RestaurantSelector;